import React from 'react';
import { Button, ButtonGroup, ButtonProps, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { ArrowDropDown, Check } from '@mui/icons-material';
import {
    SplitButtonStorageKey,
    getSplitButtonSelection,
    setSplitButtonSelection
} from './splitButtonStorage';

export interface SplitButtonOption {
    id: string;
    label: string;
    onClick: () => void;
}

interface SplitButtonProps {
    options: SplitButtonOption[];
    defaultId?: string;
    storageKey: SplitButtonStorageKey;
    size?: ButtonProps['size'];
}

export const SplitButton: React.FC<SplitButtonProps> = ({ options, defaultId, storageKey, size }) => {
    const fallbackId = defaultId || options[0]?.id || '';
    const [selectedId, setSelectedId] = React.useState(
        () => getSplitButtonSelection(storageKey) ?? fallbackId
    );
    const [menuAnchorEl, setMenuAnchorEl] = React.useState<HTMLElement | undefined>(undefined);

    const selectedOption = React.useMemo(
        () => options.find(o => o.id === selectedId) || options[0],
        [options, selectedId]
    );

    const handleMainClick = React.useCallback(() => {
        selectedOption?.onClick();
    }, [selectedOption]);

    const handleToggle = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    }, []);

    const handleClose = React.useCallback(() => {
        setMenuAnchorEl(undefined);
    }, []);

    const handleMenuItemClick = React.useCallback(
        (option: SplitButtonOption) => () => {
            setSelectedId(option.id);
            setSplitButtonSelection(storageKey, option.id);
            setMenuAnchorEl(undefined);
        },
        [storageKey]
    );

    return (
        <>
            <ButtonGroup variant="contained" size={size}>
                <Button onClick={handleMainClick} sx={{ border: 'none' }}>
                    {selectedOption?.label}
                </Button>
                <Button size="small" onClick={handleToggle}>
                    <ArrowDropDown />
                </Button>
            </ButtonGroup>
            <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleClose}>
                {options.map(option => (
                    <MenuItem key={option.id} onClick={handleMenuItemClick(option)}>
                        <ListItemIcon sx={{ visibility: option.id === selectedId ? 'visible' : 'hidden' }}>
                            <Check fontSize="small" />
                        </ListItemIcon>
                        <Typography>{option.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
